import React from 'react'
import { useDispatch, useSelector} from 'react-redux'
import { BASE_IBV } from '../redux/types/base'
import { loanStatus } from '../constants/form'
import { endpoints } from '../constants/endpoints'
import { setMessage } from '../redux/actions/message'
import { useTranslation } from 'react-i18next'
import { sendGetEndpoint } from '../services/baseApi'
import { validateIBV, updateLoan } from '../redux/actions/tier'
import { notUndefinedNull } from '../helpers/shared'
import { getVisibleFormInputs } from '../helpers/base'

export const useIbvDialog = ({ setAlert }) => {
  const [open, setOpen] = React.useState(false)
  const { requestWidgetURL } = useSelector(state => state.base.ibv) || {}
  const { userIdBifrost, dataProductRequest, lastProductRequestId } = useSelector(state => state.user) || {}
  const { loading } = useSelector(state => state.base)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  let [, chirpRequestCode] = requestWidgetURL?.split('requestCode=')
  
  /* Create 'currentProduct' along its Ref.
  ** The Ref is needed to have the latest value inside the 'message' event listener
  */
  const [currentProduct, setCurrentProduct] = React.useState(null)
  const currentProductRef = React.useRef(null);

  React.useEffect(() => {
    setTimeout(() => {
      setCurrentProduct(dataProductRequest?.filter(
        product => product.id === lastProductRequestId)[0] 
          || dataProductRequest?.[0]
      )
    }, 1000);
  }, [dataProductRequest, lastProductRequestId])

  React.useEffect(() => {
    currentProductRef.current = currentProduct;
  }, [currentProduct]);

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handlePostMessage = async function (e, currentProduct) {
    var messageFromParent = e.data
    if (messageFromParent.success) {
      try {
        let res = await sendGetEndpoint(endpoints.getChirpCodeCurrentStatus(chirpRequestCode))
        if (res) {
          let productId = currentProduct?.id
          let statusId = currentProduct?.status?.id
          let userId = currentProduct?.user?.id
          dispatch(validateIBV({
            res,
            setAlert,
            chirpRequestCode,
            userIdBifrost,
            productId,
            statusId,
            userId,
            t,
            completeCheck: true
          }))
        }
      } catch (error) {
        console.error(error)
      }
      
    }
  }

  const loadIframe = () => {
    // addEventListener support for IE8
    function bindEvent(element, eventName, eventHandler) {
      if (element.addEventListener){
          element.addEventListener(eventName, eventHandler, false);
      } else if (element.attachEvent) {
          element.attachEvent('on' + eventName, eventHandler);
      }
    }
    var iframeSource = requestWidgetURL || ''
    var iframe = document.getElementById('the_iframe')
    let iframeContainer = document.getElementById('iframe_container')
    iframe?.setAttribute('src', iframeSource);
    iframe.style.border = 'none'
    if (document.body?.offsetWidth <= 768) {
      iframe.style.width = document.body?.offsetWidth + 'px'
      iframe.style.height = document.body?.offsetHeight + 'px'
    } else {
      iframeContainer.style.height = '75vh'
      iframe.style.height = '100%'
      iframe.style.width = '100%'
    }

    bindEvent(window, 'message', function(e) {
      handlePostMessage(e, currentProductRef.current);
    });
  }

  React.useEffect(() => {
    if (open) { // when modal opens
      setTimeout(() => {
        loadIframe()
      }, 100)
    }
  }, [open])

  /* Automatically opens (or automatically submits and verifies) IBV */
  const [isAlreadyValidated, setIsAlreadyValidated] = React.useState(false)
  React.useEffect(() => {
    if (!isAlreadyValidated) {
      if (notUndefinedNull(currentProduct?.validated_ibv)) setIsAlreadyValidated(true)
      setTimeout(() => {
        if (!currentProduct?.validated_ibv) {
          const form = document.getElementsByTagName('Form')[0];
          const visibleInputs = getVisibleFormInputs(form)
          const isFormPopulated = visibleInputs.length > 0
     
          if (!isFormPopulated) setOpen(true) // Open IBV if form has no inputs
        } else {
          updateLoan({
            status: loanStatus.verified,
            requestCode: chirpRequestCode,
            bifrostUserId: userIdBifrost
          })
          /* If already validated; automatically send it */
          const btnSave = document.querySelector('button[name="btn_save"]');
          const btnNext = document.querySelector('button[name="btn_next"]');
          if(btnNext || btnSave) {
            setTimeout(() => {
              btnNext?.click()
              btnSave?.click()
            }, 1000)
          }
        }
      }, 1000)
    }
  }, [loading])


  return {
    loadIframe,
    handleClose,
    handleOpen,
    open, 
    setOpen,
    requestWidgetURL
  }
}

export const useIbvShown = () => {
  const { infoProductRequest } = useSelector(state => state.user) || {}
  const dispatch = useDispatch()
  const { requestWidgetURL, isIBVVerified } = useSelector(state => state.base.ibv) || {}

  // Case for applications with 'ibv_not_required' === true
  React.useEffect(() => {
    if (infoProductRequest?.application?.ibv_not_required) {
      dispatch({
        type: BASE_IBV,
        payload: {
          isIBVShown: false,
          isIBVRequired: false
        }
      })
    }
  }, [infoProductRequest])

  // IBV component should be hidden if there's no requestWidgetURL
  React.useEffect(() => {
    if (requestWidgetURL?.length === 0) {
      dispatch({
        type: BASE_IBV,
        payload: {
          isIBVShown: false
        }
      })
    }
  }, [requestWidgetURL])

  return {
    isIBVVerified
  }
}

export const useIbv = () => {
  // Logic for IBV below, could be moved to a custom hook
  const { t } = useTranslation()
  const { isIBVVerified, requestWidgetURL, isIBVShown } = useSelector(state => state.base.ibv) || {}
  const dispatch = useDispatch()
  const [alert, setAlert] = React.useState(false)

  const { userIdBifrost, dataProductRequest, lastProductRequestId } = useSelector(state => state.user) || {}
  let [, chirpRequestCode] = requestWidgetURL?.split('requestCode=');
  const currentProduct = dataProductRequest?.filter(product => product.id === lastProductRequestId)[0]
  const statusId = currentProduct?.status?.id
  const userId = currentProduct?.user?.id
  const productId = currentProduct?.id
  
  const checkAndUploadChirpStatus = React.useCallback(async () => {
    let res = await sendGetEndpoint(endpoints.getChirpCodeCurrentStatus(chirpRequestCode))
    if (res) {
        dispatch(validateIBV({
          res,
          setAlert,
          chirpRequestCode,
          userIdBifrost,
          productId,
          statusId,
          userId,
          t,
          completeCheck: false
        }))
      }
  }, [  
    dispatch, setAlert, chirpRequestCode, userIdBifrost,
    productId, statusId, userId, t
  ])
  
  
  React.useEffect(() => {
    if ((requestWidgetURL?.length > 0) && isIBVVerified) {
      if (alert) {
        setTimeout(() => {
          dispatch(setMessage(t('ibv.verificationSuccess'), 'success'))
        }, 600)
        setAlert(false)
      }
    } else if ((requestWidgetURL?.length > 0) && !isIBVVerified) {
      checkAndUploadChirpStatus()
    }
  }, [isIBVVerified, t, requestWidgetURL?.length, dispatch, checkAndUploadChirpStatus, alert])



  return { isIBVVerified, isIBVShown, setAlert }
}